import Experience from "@folio-2026/experience"
import { blendBrighten, blendBrightenAlpha } from "@folio-2026/shared"
import {
  abs,
  color,
  Fn,
  fract,
  max,
  mix,
  positionGeometry,
  smoothstep,
  texture,
  time,
  uniform,
  uv,
  vec2,
  vec3,
  vec4,
} from "three/tsl"
import * as THREE from "three/webgpu"
import type { FolderApi, Pane } from "tweakpane"

export default class WatercolorBrush {
  experience: Experience
  sizes: Experience["sizes"]
  pointer: Experience["pointer"]
  textScene: THREE.Scene
  bufferScene: THREE.Scene
  targetA: THREE.RenderTarget
  targetB: THREE.RenderTarget
  fbmTexture: THREE.Texture
  debugFolder?: Pane | FolderApi
  private renderScale: number

  placeholderTexture: THREE.DataTexture
  resolution: THREE.Vector2

  uniforms: {
    pointerUV: THREE.UniformNode<THREE.Vector2>
    resolution: THREE.UniformNode<THREE.Vector2>
    floodingSpeed: THREE.UniformNode<number>
    floodingDensity: THREE.UniformNode<number>
    circleRadius: THREE.UniformNode<number>
  }

  mesh!: THREE.Mesh
  quad!: THREE.Mesh
  // Texture nodes (sampled in the shader). These start as a 1×1 transparent
  // placeholder and can be swapped from `World.update()` without rebuilding.
  diffuse: THREE.TextureNode
  y!: number
  x!: number

  constructor({
    textScene,
    fbmTexture,
    debugFolder,
    renderScale = 0.33,
  }: {
    textScene: THREE.Scene
    pane?: Pane
    fbmTexture: THREE.Texture
    debugFolder?: Pane | FolderApi
    renderScale?: number
  }) {
    this.experience = Experience.getInstance()
    this.sizes = this.experience.sizes
    this.pointer = this.experience.pointer
    this.debugFolder = debugFolder

    this.textScene = textScene

    this.fbmTexture = fbmTexture

    this.bufferScene = new THREE.Scene()

    this.renderScale = this.getValidatedRenderScale(renderScale)

    const { width: internalWidth, height: internalHeight } =
      this.getInternalRenderSize()

    this.targetA = new THREE.RenderTarget(internalWidth, internalHeight)
    this.targetB = new THREE.RenderTarget(internalWidth, internalHeight)

    this.configureTarget(this.targetA)
    this.configureTarget(this.targetB)

    this.resolution = new THREE.Vector2(this.sizes.width, this.sizes.height)
    this.uniforms = {
      pointerUV: uniform(
        vec2(this.pointer.normalizedX, this.pointer.normalizedY),
      ),
      resolution: uniform(this.resolution),
      floodingSpeed: uniform(1),
      floodingDensity: uniform(1),
      circleRadius: uniform(50),
    }

    // Use a valid "null-ish" texture so node materials can compile,
    // then swap `diffuse.value` / `prev.value` from `World.update()`.
    this.placeholderTexture = new THREE.DataTexture(
      new Uint8Array([0, 0, 0, 0]),
      1,
      1,
    )

    this.diffuse = texture(this.placeholderTexture)

    this.createBounds()

    this.createQuad()

    this.setDebug()
  }

  createQuad() {
    const geometry = new THREE.PlaneGeometry(2, 2)
    const bufferMaterial = new THREE.MeshBasicNodeMaterial()
    bufferMaterial.vertexNode = vec4(
      positionGeometry.x,
      positionGeometry.y,
      0,
      1,
    )

    const vUv = uv()
    const bgColor = vec3(0)

    const circleMask = Fn(() => {
      const radiusPx = this.uniforms.circleRadius
      // This shader runs at the render target resolution. Convert ~1 internal
      // pixel to screen pixels so the edge softness stays roughly consistent
      // when rendering the targets at a lower resolution.
      const featherPx = 1 / this.renderScale
      const distPx = uv()
        .mul(this.uniforms.resolution)
        .sub(this.uniforms.pointerUV.mul(this.uniforms.resolution))
        .length()

      return smoothstep(radiusPx.sub(featherPx), radiusPx, distPx).oneMinus()
    })()
    const circleMap = mix(color(0x000000), color(0xffffff), circleMask)

    const uvAspect = vec2(
      vUv.x.mul(this.uniforms.resolution.x.div(this.uniforms.resolution.y)),
      vUv.y,
    )

    // Animate fbm UV scale with a loop (0..1..0)
    const t = fract(time.mul(0.05))
    const loop = abs(t.mul(2.0).sub(1.0)).oneMinus()
    const fbmScale = mix(0.35, 0.45, loop)

    const fbmUv = uvAspect.mul(fbmScale)
    const dispN = texture(this.fbmTexture, fbmUv).r.pow(2) // float
    const dispPx = dispN.mul(this.uniforms.resolution.x.mul(0.001)) // float (≈ old x-amplitude, in pixels)
    const dispUv = vec2(
      dispPx.div(this.uniforms.resolution.x),
      dispPx.div(this.uniforms.resolution.y),
    )

    // `this.diffuse` is a render target texture, which is vertically flipped when
    // sampled with default UVs. Flip Y here so the feedback loop doesn't mirror.
    const diffuseUv = vec2(vUv.x, vUv.y.oneMinus())

    const texel = this.diffuse.sample(diffuseUv)
    const texel2 = this.diffuse.sample(
      vec2(diffuseUv.x.add(dispUv.x), diffuseUv.y),
    )
    const texel3 = this.diffuse.sample(
      vec2(diffuseUv.x.sub(dispUv.x), diffuseUv.y),
    )
    // note: because we flipped Y, vertical offsets invert direction
    const texel4 = this.diffuse.sample(
      vec2(diffuseUv.x, diffuseUv.y.sub(dispUv.y)),
    )
    const texel5 = this.diffuse.sample(
      vec2(diffuseUv.x, diffuseUv.y.add(dispUv.y)),
    )

    let floodColor = texel.rgb
    floodColor = blendBrighten(floodColor, texel2.rgb)
    floodColor = blendBrighten(floodColor, texel3.rgb)
    floodColor = blendBrighten(floodColor, texel4.rgb)
    floodColor = blendBrighten(floodColor, texel5.rgb)

    const waterColor = blendBrightenAlpha(
      texel.rgb,
      floodColor.mul(this.uniforms.floodingDensity),
      this.uniforms.floodingSpeed,
    )

    // fade: subtract a constant each frame, then clamp to 0
    const fadePerFrame = 0.005 // increase to fade faster
    const decay = 0.99 // optional

    const waterColorFaded = max(
      bgColor,
      waterColor.mul(decay).sub(fadePerFrame),
    )

    bufferMaterial.colorNode = circleMap.add(waterColorFaded)

    this.quad = new THREE.Mesh(geometry, bufferMaterial)

    this.bufferScene.add(this.quad)
  }

  private createBounds() {
    this.y = 50
    this.x = 50
  }

  setDebug() {
    if (!this.debugFolder) return

    const folder = this.debugFolder.addFolder({ title: "🖌️ WatercolorBrush" })

    const settings = {
      floodingSpeed: this.uniforms.floodingSpeed.value,
      floodingDensity: this.uniforms.floodingDensity.value,
      circleRadius: this.uniforms.circleRadius.value,
    }

    folder
      .addBinding(settings, "floodingSpeed", {
        min: 0,
        max: 2,
        step: 0.01,
      })
      .on("change", ({ value }) => {
        this.uniforms.floodingSpeed.value = value
      })

    folder
      .addBinding(settings, "floodingDensity", {
        min: 0.8,
        max: 1,
        step: 0.001,
      })
      .on("change", ({ value }) => {
        this.uniforms.floodingDensity.value = value
      })

    folder
      .addBinding(settings, "circleRadius", {
        min: 1,
        max: 200,
        step: 1,
      })
      .on("change", ({ value }) => {
        this.uniforms.circleRadius.value = value
      })
  }

  setDiffuseTexture(tex: THREE.Texture | null | undefined) {
    ;(this.diffuse as unknown as { value: THREE.Texture }).value = (tex ??
      this.placeholderTexture) as THREE.Texture
  }

  update() {
    this.uniforms.pointerUV.value.x = this.pointer.normalizedX
    this.uniforms.pointerUV.value.y = this.pointer.normalizedY
  }

  resize() {
    const { width: internalWidth, height: internalHeight } =
      this.getInternalRenderSize()

    this.targetA.setSize(internalWidth, internalHeight)
    this.targetB.setSize(internalWidth, internalHeight)

    // Keep "pixel space" uniforms expressed in screen pixels.
    this.resolution.set(this.sizes.width, this.sizes.height)
    this.uniforms.resolution.value = this.resolution
  }

  private getInternalRenderSize() {
    const width = Math.max(1, Math.floor(this.sizes.width * this.renderScale))
    const height = Math.max(1, Math.floor(this.sizes.height * this.renderScale))

    return { width, height }
  }

  private getValidatedRenderScale(renderScale: number) {
    if (!Number.isFinite(renderScale)) return 0.33
    if (renderScale <= 0) return 0.33

    return Math.min(1, renderScale)
  }

  private configureTarget(target: THREE.RenderTarget) {
    target.texture.magFilter = THREE.LinearFilter
    target.texture.minFilter = THREE.LinearFilter
    target.texture.generateMipmaps = false
  }
}
